import { DateFormat, enhancedFetch, Icon, ssrBuilder, TabButton, TabButtons, TabPanel, Tabs, useFetch } from 'journey-ui'
import { NextPageContext } from 'next'
import React from 'react'
import { Helmet } from 'react-helmet'
import { MapPinLine, PhoneCall } from 'phosphor-react'
import { AppPage } from '../components/AppPage'
import { Mapbox } from '../components/Location/Mapbox'
import { BrandHeader } from '../components/BrandHeader'
import { redirectIfNoBrand } from '../helpers'

const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

interface Props {
  businessHours: BusinessHour[]
  brand: Brand
}

const BusinessHourList = ({ hours, filter }: { hours: BusinessHour[], filter: (block: BusinessHourBlock) => boolean }) => (
  <div className="space-y-2">
    {dayNames.map((dayName) => (
      <div key={dayName} className="flex justify-between">
        <div>
          {dayName}
        </div>
        <div>
          {hours
            .filter((h) => h.day_name === dayName)
            .flatMap((hour) => hour.blocks)
            .filter((ele) => filter(ele))
            .sort((a, b) => (new Date(a.open_time)).getTime() - (new Date(b.open_time)).getTime())
            .map((block) => (
              <div className="flex-shrink-0 flex items-center space-x-2" key={block.hash_id}>
                {block.is_closed ? <>Closed</> : (
                  <>
                    <DateFormat value={block.open_time} format="h:mma" />
                    <span>-</span>
                    <DateFormat value={block.close_time} format="h:mma" />
                  </>
                )}
              </div>
            ))}
        </div>
      </div>
    ))}
  </div>
)

const LocationPage: React.FC<Props> = ({ brand, businessHours }) => {
  const deliveryBusinessHours = businessHours.filter((bh) => bh.blocks.find((b) => b.type === 'delivery'))
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    logo: brand.logo,
    image: brand.background,
    name: brand.name,
    address: {
      '@type': 'PostalAddress',
      streetAddress: brand.address,
      addressLocality: brand.city_name,
      addressRegion: brand.state_abbreviation,
      postalCode: brand.zip_code,
      addressCountry: brand.country,
    },
    geo: {
      latitude: brand.latitude,
      longitude: brand.longitude,
    },
    url: brand.website_url,
    telephone: brand.phone_number,
    servesCuisine: 'Pizza',
    priceRange: '$$',
    openingHoursSpecification: [],
    menu: `${brand.website_url}/menu`,
    acceptsReservations: 'True',
  }
  businessHours.forEach((businessHour) => {
    businessHour.blocks.forEach((block) => {
      structuredData.openingHoursSpecification.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          businessHour.day_name,
        ],
        opens: block.open_time,
        closes: block.close_time,
        disambiguatingDescription: block.type,
      })
    })
  })
  return (
    <AppPage brand={brand} title="Directions & Business Hours" description={`Located at ${brand.location_display}.`} className="location-page">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <div className="flex sm:flex-nowrap flex-wrap w-full h-full">
        <Mapbox lat={parseFloat(brand.latitude)} lng={parseFloat(brand.longitude)} style={{ width: '100%', height: '100%' }} />

        <div className="bg-white w-80 p-3 rounded space-y-2 flex-shrink-0 absolute sm:relative sm:top-0 sm:right-0 top-[100px] right-5">
          <div className="flex items-center space-x-2">
            <Icon src={PhoneCall} />
            <span>{brand.phone_number}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon src={MapPinLine} />
            <span>{brand.location_display}</span>
          </div>
          <Tabs initialTab="pickup" urlPropName="hours">
            <TabButtons className="px-1 mb-2 segment">
              <TabButton name="pickup" className="justify-center">Pickup Hours</TabButton>
              {brand.show_delivery_option && deliveryBusinessHours.length > 0 ? <TabButton name="delivery" className="justify-center">Delivery Hours</TabButton> : undefined}
            </TabButtons>
            <TabPanel name="pickup">
              <BusinessHourList
                hours={businessHours}
                filter={(b) => b.type === 'pickup'}
              />
            </TabPanel>
            <TabPanel name="delivery">
              <BusinessHourList
                hours={businessHours}
                filter={(b) => b.type === 'delivery'}
              />
            </TabPanel>
          </Tabs>
        </div>
      </div>
    </AppPage>
  )
}

// noinspection JSUnusedGlobalSymbols (PHPStorm doesn't detect NextJS's pages)
export default LocationPage

export async function getServerSideProps(ctx) {
  return ssrBuilder(ctx)
    .setProp('brand', (fetcher) => fetcher(`/api/brand/${ctx.req.headers.host}`))
    .setProp('businessHours', (fetcher) => fetcher(`/api/brand/${ctx.req.headers.host}/business-hours`))
    .onError(redirectIfNoBrand)
    .get()
}
